import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { EditLayer, Field, LayerButtons, Notice, ReadonlyValue, Switch } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { CreateModal } from './CreateModal'
import {
  createProduct,
  flattenTree,
  updateProduct,
  useCategories,
  useProductOptions,
  type ProductCard,
  type ProductRequest,
} from './productApi'
import { useEffectiveTaxRates } from './taxRateApi'

/**
 * Форма товара целиком (V60): заведение (`/products/new`) и правка на карточке.
 *
 * ## Что здесь главное
 *
 * **Единица, тип и признаки учёта после первого движения не правятся.** Сервер отвечает
 * на такую правку `product.accounting_locked`; форма не ждёт отказа и показывает эти поля
 * замком с причиной (`accountingLocked` в карточке). Поле, которое выглядит правимым и
 * гарантированно отказывает при сохранении, учит игнорировать отказы.
 *
 * **Правка уходит только изменёнными полями**, пустое — пустой строкой (`''` — «очистить»,
 * отсутствие ключа — «не менялось»): стёртый вес, отправленный `null`, сервер счёл бы
 * неизменным, и карточка рапортовала бы «сохранено» при прежнем весе (#98).
 *
 * **Партии и серийные номера — один выбор из трёх**, а не два флажка: товар не бывает
 * одновременно партионным и серийным (CHECK в схеме), и два флажка позволяли бы собрать
 * состояние, которое сервер всё равно отвергнет.
 */

export interface ProductDraft {
  sku: string
  name: string
  description: string
  productType: string
  categoryId: string
  uomId: string
  taxRateCode: string
  barcode: string
  weightKg: string
  volumeM3: string
  lengthMm: string
  widthMm: string
  heightMm: string
  tracking: 'none' | 'batch' | 'serial'
  marked: boolean
  shelfLifeDays: string
  storageCondition: string
  minStock: string
  reorderPoint: string
  active: boolean
}

const text = (value: number | string | null | undefined) => (value == null ? '' : String(value))

export function draftOf(card: ProductCard): ProductDraft {
  return {
    sku: card.sku,
    name: card.name,
    description: card.description ?? '',
    productType: card.productType,
    categoryId: card.categoryId ?? '',
    uomId: card.uomId,
    taxRateCode: card.taxRateCode,
    barcode: card.barcode ?? '',
    weightKg: text(card.weightKg),
    volumeM3: text(card.volumeM3),
    lengthMm: text(card.lengthMm),
    widthMm: text(card.widthMm),
    heightMm: text(card.heightMm),
    tracking: card.batchTracked ? 'batch' : card.serialTracked ? 'serial' : 'none',
    marked: card.marked,
    shelfLifeDays: text(card.shelfLifeDays),
    storageCondition: card.storageCondition,
    minStock: text(card.minStock),
    reorderPoint: text(card.reorderPoint),
    active: card.active,
  }
}

export const EMPTY_DRAFT: ProductDraft = {
  sku: '', name: '', description: '', productType: 'goods', categoryId: '', uomId: '',
  taxRateCode: 'vat_standard', barcode: '', weightKg: '', volumeM3: '', lengthMm: '', widthMm: '',
  heightMm: '', tracking: 'none', marked: false, shelfLifeDays: '', storageCondition: 'ambient',
  minStock: '', reorderPoint: '', active: true,
}

const TEXT_FIELDS = [
  'sku', 'name', 'description', 'productType', 'categoryId', 'uomId', 'taxRateCode', 'barcode',
  'weightKg', 'volumeM3', 'lengthMm', 'widthMm', 'heightMm', 'shelfLifeDays', 'storageCondition',
  'minStock', 'reorderPoint',
] as const

/**
 * Тело запроса: при заведении — всё заполненное, при правке — только изменённое.
 *
 * Описание уходит как есть, с переводами строк: это абзацы, а не одна строка, и обрезать
 * в нём можно только края.
 */
export function requestOf(draft: ProductDraft, base: ProductDraft | null): ProductRequest {
  const body: Record<string, unknown> = {}
  for (const field of TEXT_FIELDS) {
    const value = field === 'description' ? draft[field] : draft[field].trim()
    if (base == null) {
      if (value.trim() !== '') body[field] = value
    } else if (value !== (field === 'description' ? base[field] : base[field].trim())) {
      body[field] = value
    }
  }
  const batch = draft.tracking === 'batch'
  const serial = draft.tracking === 'serial'
  if (base == null || draft.tracking !== base.tracking) {
    body.batchTracked = batch
    body.serialTracked = serial
  }
  if (base == null || draft.marked !== base.marked) body.marked = draft.marked
  if (base == null || draft.active !== base.active) body.active = draft.active
  return body as ProductRequest
}

const NUMBER = /^\d+([.,]\d+)?$/
const INTEGER = /^\d+$/

/**
 * Ошибки полей, видные до сервера: не-числа в числовых. Пустые обязательные поля сюда не
 * входят — свежая форма, вся в красном, читается как уже допущенная ошибка; о них говорит
 * причина рядом с кнопкой (`missingRequired`).
 */
export function draftErrors(draft: ProductDraft, t: (key: string) => string): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of ['weightKg', 'volumeM3', 'minStock', 'reorderPoint'] as const) {
    if (draft[field].trim() !== '' && !NUMBER.test(draft[field].trim())) errors[field] = t('product.form.number')
  }
  for (const field of ['lengthMm', 'widthMm', 'heightMm', 'shelfLifeDays'] as const) {
    if (draft[field].trim() !== '' && !INTEGER.test(draft[field].trim())) errors[field] = t('product.form.integer')
  }
  return errors
}

export const missingRequired = (draft: ProductDraft) => draft.sku.trim() === '' || draft.name.trim() === ''

/** Поля формы по секциям; одна разметка на заведение и правку. */
export function ProductFields({ draft, onChange, locked, errors }: {
  draft: ProductDraft
  onChange: (draft: ProductDraft) => void
  /** Признаки учёта заморожены движениями. */
  locked: boolean
  errors: Record<string, string>
}) {
  const { t } = useApp()
  const prefix = useId()
  const options = useProductOptions()
  const categories = useCategories()
  const taxRates = useEffectiveTaxRates()
  const tree = useMemo(() => flattenTree(categories.data ?? []), [categories.data])
  const set = <K extends keyof ProductDraft>(field: K, value: ProductDraft[K]) => onChange({ ...draft, [field]: value })
  const id = (field: string) => `${prefix}-${field}`

  const input = (field: keyof ProductDraft & string, label: string, extra?: {
    mono?: boolean; inputMode?: 'decimal' | 'numeric'; required?: boolean; help?: string
  }) => (
    <Field label={label} htmlFor={id(field)} required={extra?.required} error={errors[field]} help={extra?.help}>
      <input
        id={id(field)}
        className={extra?.mono ? 'input mono' : 'input'}
        inputMode={extra?.inputMode}
        value={draft[field] as string}
        aria-invalid={errors[field] ? true : undefined}
        onChange={(event) => set(field, event.target.value as never)}
      />
    </Field>
  )

  const unitName = options.data?.units.find((unit) => unit.id === draft.uomId)?.name ?? draft.uomId
  const lockedHelp = t('product.form.lockedHelp')
  const taxCodes = taxRates.data ?? []
  const taxKnown = taxCodes.some((rate) => rate.code === draft.taxRateCode)

  return (
    <div className="stack" style={{ gap: 18 }}>
      {locked && (
        <div className="alert alert-info" role="note">
          <Icon name="lock" />
          <span className="fg">{t('product.form.lockedNote')}</span>
        </div>
      )}

      <section aria-labelledby={id('s-main')} className="stack" style={{ gap: 10 }}>
        <h3 id={id('s-main')} className="overline">{t('product.form.section.main')}</h3>
        <div className="form-grid">
          {input('sku', t('column.sku'), { mono: true, required: true })}
          {input('name', t('column.name'), { required: true })}
          <Field label={t('column.product_type')} htmlFor={id('productType')} help={locked ? lockedHelp : undefined}>
            {locked ? (
              <ReadonlyValue>{t(`value.sales.product.product_type.${draft.productType}`)}</ReadonlyValue>
            ) : (
              <select id={id('productType')} className="select" value={draft.productType}
                      onChange={(event) => set('productType', event.target.value)}>
                {(options.data?.productTypes ?? [draft.productType]).map((code) => (
                  <option key={code} value={code}>{t(`value.sales.product.product_type.${code}`)}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('column.category_id')} htmlFor={id('categoryId')}>
            {/* Дерево — отступом в выпадающем списке: выбор один, а путь виден по отступам. */}
            <select id={id('categoryId')} className="select" value={draft.categoryId}
                    onChange={(event) => set('categoryId', event.target.value)}>
              <option value="">{t('product.form.noCategory')}</option>
              {tree.map(({ category, depth }) => (
                <option key={category.id} value={category.id}>{`${' '.repeat(depth * 3)}${category.name}`}</option>
              ))}
            </select>
          </Field>
          <Field label={t('column.uom_id')} htmlFor={id('uomId')} help={locked ? lockedHelp : undefined}>
            {locked ? (
              <ReadonlyValue>{unitName}</ReadonlyValue>
            ) : (
              <select id={id('uomId')} className="select" value={draft.uomId}
                      onChange={(event) => set('uomId', event.target.value)}>
                {draft.uomId === '' && <option value="">{t('product.form.uomDefault')}</option>}
                {(options.data?.units ?? []).map((unit) => (
                  <option key={unit.id} value={unit.id}>{`${unit.name} (${unit.code})`}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('column.tax_rate_code')} htmlFor={id('taxRateCode')}>
            <select id={id('taxRateCode')} className="select" value={draft.taxRateCode}
                    onChange={(event) => set('taxRateCode', event.target.value)}>
              {!taxKnown && <option value={draft.taxRateCode}>{draft.taxRateCode}</option>}
              {taxCodes.map((rate) => (
                <option key={rate.id} value={rate.code}>{`${rate.name} — ${rate.percent} %`}</option>
              ))}
            </select>
          </Field>
          <Field label={t('column.is_active')}>
            <Switch checked={draft.active} onChange={(value) => set('active', value)}
                    label={draft.active ? t('record.product.active') : t('record.product.inactive')} />
          </Field>
        </div>
      </section>

      <section aria-labelledby={id('s-description')} className="stack" style={{ gap: 10 }}>
        <h3 id={id('s-description')} className="overline">{t('product.form.section.description')}</h3>
        <Field label={t('product.form.description')} htmlFor={id('description')} help={t('product.form.descriptionHelp')} span2>
          <textarea id={id('description')} className="input" rows={6} value={draft.description}
                    onChange={(event) => set('description', event.target.value)} />
        </Field>
      </section>

      <section aria-labelledby={id('s-accounting')} className="stack" style={{ gap: 10 }}>
        <h3 id={id('s-accounting')} className="overline">{t('product.form.section.accounting')}</h3>
        <div className="form-grid">
          <Field label={t('product.form.tracking')} help={locked ? lockedHelp : t('product.form.trackingHelp')} span2>
            {locked ? (
              <ReadonlyValue>{t(`product.tracking.${draft.tracking}`)}</ReadonlyValue>
            ) : (
              <div className="seg" role="group" aria-label={t('product.form.tracking')} style={{ alignSelf: 'flex-start' }}>
                {(['none', 'batch', 'serial'] as const).map((mode) => (
                  <button key={mode} type="button" aria-pressed={draft.tracking === mode} onClick={() => set('tracking', mode)}>
                    {t(`product.tracking.${mode}`)}
                  </button>
                ))}
              </div>
            )}
          </Field>
          <Field label={t('column.storage_condition')} htmlFor={id('storageCondition')}>
            <select id={id('storageCondition')} className="select" value={draft.storageCondition}
                    onChange={(event) => set('storageCondition', event.target.value)}>
              {(options.data?.storageConditions ?? [draft.storageCondition]).map((code) => (
                <option key={code} value={code}>{t(`value.sales.product.storage_condition.${code}`)}</option>
              ))}
            </select>
          </Field>
          {input('shelfLifeDays', t('product.form.shelfLifeDays'), { inputMode: 'numeric' })}
          {input('minStock', t('column.min_stock'), { inputMode: 'decimal' })}
          {input('reorderPoint', t('product.form.reorderPoint'), { inputMode: 'decimal', help: t('product.form.reorderHelp') })}
        </div>
      </section>

      <section aria-labelledby={id('s-logistics')} className="stack" style={{ gap: 10 }}>
        <h3 id={id('s-logistics')} className="overline">{t('card.section.logistics')}</h3>
        <div className="form-grid">
          {input('weightKg', t('column.weight_kg'), { inputMode: 'decimal' })}
          {input('volumeM3', t('product.form.volumeM3'), { inputMode: 'decimal' })}
          {input('lengthMm', t('product.form.lengthMm'), { inputMode: 'numeric' })}
          {input('widthMm', t('product.form.widthMm'), { inputMode: 'numeric' })}
          {input('heightMm', t('product.form.heightMm'), { inputMode: 'numeric' })}
          {input('barcode', t('column.barcode'), { mono: true, inputMode: 'numeric' })}
        </div>
      </section>

      <section aria-labelledby={id('s-marking')} className="stack" style={{ gap: 10 }}>
        <h3 id={id('s-marking')} className="overline">{t('product.form.section.marking')}</h3>
        <Field label={t('column.is_marked')} help={locked ? lockedHelp : t('product.form.markedHelp')}>
          {locked ? (
            <ReadonlyValue>{draft.marked ? t('adm.on') : t('adm.off')}</ReadonlyValue>
          ) : (
            <Switch checked={draft.marked} onChange={(value) => set('marked', value)}
                    label={draft.marked ? t('product.form.marked.on') : t('product.form.marked.off')} />
          )}
        </Field>
      </section>
    </div>
  )
}

/** Отказ сервера его словами: там названа причина («артикул занят», «по товару есть движения»). */
const failure = (error: unknown, fallback: string) => (error instanceof ApiError ? error.message : fallback)

/** Заведение товара: `/products/new`. */
export function ProductCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT)
  const [notice, setNotice] = useState<string | null>(null)
  const errors = draftErrors(draft, t)

  const save = useMutation({
    mutationFn: () => createProduct(requestOf(draft, null)),
    onSuccess: (card) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.product'] })
      navigate(`/products/${card.id}`)
    },
    onError: (error) => setNotice(failure(error, t('app.error'))),
  })

  if (!can(user, 'sales.product', 'create')) return <AccessDenied />

  const blocked = missingRequired(draft)
    ? t('product.form.needSkuName')
    : Object.keys(errors).length > 0 ? t('product.form.fix') : null
  return (
    <CreateModal
      overline={t('nav.products')}
      title={t('product.create.title')}
      error={notice}
      blockedReason={blocked}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('product.create.submit')}
      onCancel={() => navigate('/products')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <ProductFields draft={draft} onChange={setDraft} locked={false} errors={errors} />
    </CreateModal>
  )
}

/** Правка товара на карточке — боковая панель, как в администрировании. */
export function ProductEditSheet({ card, onClose, onSaved }: {
  card: ProductCard
  onClose: () => void
  onSaved: (card: ProductCard) => void
}) {
  const { t } = useApp()
  const formId = useId()
  const base = useMemo(() => draftOf(card), [card])
  const [draft, setDraft] = useState<ProductDraft>(base)
  const [notice, setNotice] = useState<string | null>(null)
  const errors = draftErrors(draft, t)
  const body = requestOf(draft, base)
  const unchanged = Object.keys(body).length === 0

  const save = useMutation({
    mutationFn: () => updateProduct(card.id, { ...body, version: card.version }),
    onSuccess: onSaved,
    onError: (error) =>
      setNotice(error instanceof ApiError && error.status === 409 ? t('product.form.conflict') : failure(error, t('app.error'))),
  })

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('nav.products')}
      title={t('product.edit.title')}
      subtitle={card.name}
      onClose={onClose}
      footer={
        <LayerButtons
          onCancel={onClose}
          saveLabel={t('product.edit.save')}
          pending={save.isPending}
          disabled={unchanged || missingRequired(draft) || Object.keys(errors).length > 0}
          form={formId}
        />
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          setNotice(null)
          save.mutate()
        }}
      >
        <Notice text={notice} />
        <ProductFields draft={draft} onChange={setDraft} locked={card.accountingLocked} errors={errors} />
      </form>
    </EditLayer>
  )
}
